import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { APITypes, Dynamo, Reports } from '../../primary-record-types';
import { getIntl } from '../../utils/intl';
import useNotify from '../useNotify';
import urls from '../../utils/urls';
import { appAPI } from '../../utils/api';
import {
    getProfileProcessingReportsKey,
    getSharedReportsCacheKey,
    onProcessReportInitiated,
    onSharedReportUpdate,
} from './cacheKeys';
import useQueryCallbacks, { useQueryErrorNotify } from '../useQueryCallbacks';
import { useRef } from 'react';
import { useHasPermission } from '../../components/v2BaseComponents/Permissions/hooks';

const intl = getIntl('useReports', {
    generateReportError: 'Error starting report generation',
    generateReportSuccess: 'Report creation started.  It may take a few seconds to complete.',
    reportCreationError: 'Error creating report',
    reportCreationSuccess: 'Report successfully created.  Click the report to view.',
    reportPollingError: 'Error polling for report status',
    getReportError: 'Error getting report download',
    errorLoadingSharedReports: 'Error loading shared reports',
    createSharedReportError: 'Error creating shared report',
    createSharedReportSuccess: 'Shared report successfully created',
    deleteSharedReportError: 'Error deleting shared report',
    deleteSharedReportSuccess: 'Shared report successfully deleted',
});

export type UseReportsArgs = {
    medicalProfileId: string | null;
    sharedReportId?: string;
    onCreateSharedReport?: (sharedReport: Dynamo.SharedReport) => void;
    onDeleteSharedReport?: () => void;
    notifyOnProcessingComplete?: boolean;
};

export type GenerateReportArgs = {
    request: APITypes.reportService.GenerateReportRequest;
};

export type ReportItem = {
    reportType: Reports.ReportType;
    status: 'pending' | 'complete' | 'failed';
    downloadUrl: string | null;
};

export type useReportsReturnValue = {
    generateReport: (args: GenerateReportArgs) => void;
    isGeneratingReport: boolean;
    reports: APITypes.reportService.ReportItem[];
    isLoadingReports: boolean;
    sharedReports: APITypes.reportService.SharedReportItem[];
    isLoadingSharedReports: boolean;
    createSharedReport: (reportType: Reports.ReportType) => void;
    isCreatingSharedReport: boolean;
    deleteSharedReport: (sharedReportId: string) => void;
    isDeletingSharedReport: boolean;
    sharedReport: APITypes.reportService.SharedReportItem | null;
    isLoadingSharedReport: boolean;
};

export const useReports = ({
    medicalProfileId,
    onCreateSharedReport,
    onDeleteSharedReport,
    sharedReportId,
    notifyOnProcessingComplete,
}: UseReportsArgs): useReportsReturnValue => {
    const hasViewPermission = useHasPermission(medicalProfileId, 'reports', 'view');
    const queryClient = useQueryClient();
    const notify = useNotify();
    const reportsQueryState = queryClient.getQueryState<APITypes.reportService.GetReportsResponse>(
        getProfileProcessingReportsKey(medicalProfileId || ''),
    );
    const hasReportsStillProcessing = reportsQueryState?.data?.reports?.some((_) => _.status === 'pending') || false;
    const previousReports = useRef<APITypes.reportService.ReportItem[]>([]);

    const reports = useQuery({
        queryKey: getProfileProcessingReportsKey(medicalProfileId || ''),
        queryFn: async () => {
            return appAPI.get<APITypes.reportService.GetReportsResponse>(urls.api.reports.reports(medicalProfileId!));
        },
        refetchInterval: hasReportsStillProcessing ? 3000 : undefined,
        enabled: hasViewPermission && !!medicalProfileId,
    });

    useQueryCallbacks({
        query: reports,
        onSuccess: (data) => {
            if (data.reports.length && previousReports.current.length && notifyOnProcessingComplete) {
                const newCompletions = data.reports.filter(
                    (curReport) =>
                        curReport.status === 'complete' &&
                        !previousReports.current.find(
                            (prevReport) =>
                                prevReport.status === 'complete' && prevReport.reportType === curReport.reportType,
                        ),
                );
                if (newCompletions.length) {
                    notify('success', intl('reportCreationSuccess'));
                }

                const newError = data.reports.find(
                    (curReport) =>
                        curReport.status === 'failed' &&
                        !previousReports.current.find(
                            (prevReport) =>
                                prevReport.status === 'failed' && prevReport.reportType === curReport.reportType,
                        ),
                );
                if (newError) {
                    notify('error', intl('reportCreationError'));
                }
            }

            previousReports.current = data.reports;
        },
    });

    const sharedReportsQuery = useQuery({
        queryKey: getSharedReportsCacheKey(medicalProfileId || ''),
        queryFn: async () => {
            return appAPI.get<APITypes.reportService.GetProfileSharedReportsResponse>(
                urls.api.reports.sharedReports(medicalProfileId!),
            );
        },
        enabled: hasViewPermission && !!medicalProfileId,
    });
    useQueryErrorNotify(sharedReportsQuery, intl('errorLoadingSharedReports'));

    const createSharedReportMutation = useMutation({
        mutationFn: async (reportType: Reports.ReportType) => {
            if (!medicalProfileId) {
                throw new Error('medicalProfileId is required to create a shared report');
            }

            return appAPI.post<APITypes.reportService.CreateSharedReportResponse>(
                urls.api.reports.sharedReports(medicalProfileId),
                {
                    body: { reportType },
                },
            );
        },
        onSuccess: (data) => {
            notify('success', intl('createSharedReportSuccess'));
            onSharedReportUpdate.resetCache({ medicalProfileId: medicalProfileId! })();
            onCreateSharedReport?.(data.sharedReport);
        },
        onError: () => {
            notify('error', intl('createSharedReportError'));
        },
    });

    const deleteSharedReportMutation = useMutation({
        mutationFn: async (sharedReportId: string) => {
            if (!medicalProfileId) {
                throw new Error('medicalProfileId is required to delete a shared report');
            }

            return appAPI.del(urls.api.reports.sharedReport(medicalProfileId, sharedReportId));
        },
        onSuccess: () => {
            notify('success', intl('deleteSharedReportSuccess'));
            sharedReportsQuery.refetch();
            onSharedReportUpdate.resetCache({ medicalProfileId: medicalProfileId! })();
            onDeleteSharedReport?.();
        },
        onError: () => {
            notify('error', intl('deleteSharedReportError'));
        },
    });

    const generateReportMutation = useMutation<any, any, GenerateReportArgs>({
        mutationFn: async ({ request }: GenerateReportArgs) => {
            if (!medicalProfileId) {
                throw new Error('medicalProfileId is required to generate a report');
            }

            return appAPI.post(urls.api.reports.medicalProfileReport(medicalProfileId), {
                body: request,
            });
        },
        onSuccess: (_response, request) => {
            notify('success', intl('generateReportSuccess'));
            onProcessReportInitiated.resetCache({ medicalProfileId: medicalProfileId! })();
            queryClient.setQueryData<APITypes.reportService.GetReportsResponse>(
                getProfileProcessingReportsKey(medicalProfileId!),
                (oldData) => {
                    return {
                        reports: [
                            ...(oldData?.reports || []).filter((_) => _.reportType !== request.request.type),
                            {
                                reportType: request.request.type,
                                status: 'pending',
                            },
                        ],
                    };
                },
            );
        },
        onError: () => {
            notify('error', intl('generateReportError'));
        },
    });

    return {
        generateReport: generateReportMutation.mutate,
        isGeneratingReport: generateReportMutation.isPending,
        reports: reports.data?.reports || [],
        isLoadingReports: reports.isLoading,
        createSharedReport: createSharedReportMutation.mutate,
        isCreatingSharedReport: createSharedReportMutation.isPending,
        deleteSharedReport: deleteSharedReportMutation.mutate,
        isDeletingSharedReport: deleteSharedReportMutation.isPending,
        sharedReport: sharedReportsQuery.data?.sharedReports?.find((_) => _.sharedReportId === sharedReportId) || null,
        isLoadingSharedReport: sharedReportsQuery.isLoading,
        sharedReports: sharedReportsQuery.data?.sharedReports || [],
        isLoadingSharedReports: sharedReportsQuery.isLoading,
    };
};
